import { JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import MenuItem from "@mui/material/MenuItem";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";

import ActionButton from "lib/components/button/ActionButton";

import { UserRole, UserRoleKey } from "organisation/OrganisationTypes";
import { Overview } from "./OrganisationUserTypes";

export type OrganisationUsersRowProps = {
	user: Overview;
	disabled?: boolean;

	onDelete: (id: number) => Promise<void>;
	onChangeRole: (id: number, role: UserRoleKey) => void;
};

export default function OrganisationUsersRow(
	props: Readonly<OrganisationUsersRowProps>
): JSX.Element {
	const { t } = useTranslation();

	const deleteMember = useCallback(
		(): Promise<void> => props.onDelete(props.user.id),
		[props.onDelete, props.user.id]
	);

	const changeMemberRole = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			props.onChangeRole(
				props.user.id,
				event.target.value as UserRoleKey
			);
		},
		[props.onChangeRole, props.user.id]
	);

	return (
		<TableRow>
			<TableCell>
				<Typography>{props.user.email}</Typography>
			</TableCell>
			<TableCell padding="none">
				<TextField
					sx={{ paddingRight: 1 }}
					inputProps={{ sx: { paddingTop: 1 } }}
					select
					variant="standard"
					size="small"
					fullWidth
					value={props.user.role}
					disabled={props.disabled}
					onChange={changeMemberRole}>
					{Object.values(UserRole).map(role => (
						<MenuItem key={role} value={role}>
							{t(role)}
						</MenuItem>
					))}
				</TextField>
			</TableCell>
			<TableCell padding="none">
				{!props.disabled && (
					<ActionButton
						icon={<PersonRemoveOutlinedIcon />}
						tooltip={t("delete")}
						dialogTitle={t("deleteDocumentTitle", {
							document: "user",
							count: 1
						})}
						dialogText={t("deleteDocumentMessage", {
							document: "user",
							count: 1
						})}
						dialogCancelLabel={t("cancel")}
						dialogConfirmLabel={t("delete")}
						onAction={deleteMember}
					/>
				)}
			</TableCell>
		</TableRow>
	);
}
